import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/frontend/contexts/AuthContext'

interface AuthGuardProps {
	children: ReactNode
	fallback?: ReactNode
	requireCompany?: boolean
}

/**
 * AuthGuard component that protects routes requiring authentication
 * Redirects to sign-in page if user is not authenticated
 * Can optionally skip company requirement for specific routes
 */
export default function AuthGuard({ children, fallback, requireCompany = true }: AuthGuardProps) {
	const { isLoaded, isSignedIn } = useAuth()
	const location = useLocation()

	// Show loading spinner while Firebase Auth is initializing
	if (!isLoaded) {
		return (
			<div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-purple-50">
				<div className="flex flex-col items-center gap-4">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
					<p className="text-gray-600 text-sm">Loading application...</p>
				</div>
			</div>
		)
	}

	// If user is not signed in, redirect to sign-in page
	if (!isSignedIn) {
		return fallback ? fallback : <Navigate to="/sign-in" replace />
	}

	// Skip company requirement for company selection route
	if (location.pathname === '/select-company') {
		return <>{children}</>
	}

	// For routes that require company selection, this will be handled by CompanyGuard
	// User is authenticated, render children
	return <>{children}</>
}
